import { CheckCircle2, CircleAlert, Info, X } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type ReactNode,
} from "react";

import { nextNoticeId } from "./Notifications.utils.js";

type NoticeTone = "success" | "error" | "info";

interface Notice {
  id: number;
  tone: NoticeTone;
  message: string;
  action?: { label: string; onClick: () => void };
}

interface NotificationsContextValue {
  notify: (
    message: string,
    tone?: NoticeTone,
    action?: { label: string; onClick: () => void },
  ) => number;
  dismiss: (id: number) => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

const AUTO_DISMISS_MS = 4500;

function NoticeItem({ notice, onDismiss }: { notice: Notice; onDismiss: (id: number) => void }) {
  // Fehlermeldungen blenden nicht automatisch aus (WCAG 2.2.1) – sie bleiben,
  // bis der Nutzer sie aktiv schließt. Erfolgs-/Info-Meldungen laufen weiter
  // per Timer aus, der bei Hover oder Tastaturfokus pausiert.
  const autoDismiss = notice.tone !== "error";
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingRef = useRef(AUTO_DISMISS_MS);
  const startedAtRef = useRef(0);
  const hoveredRef = useRef(false);
  const focusedRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    if (!autoDismiss || timeoutRef.current !== null || remainingRef.current <= 0) return;
    startedAtRef.current = Date.now();
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      onDismiss(notice.id);
    }, remainingRef.current);
  }, [autoDismiss, notice.id, onDismiss]);

  const pauseTimer = useCallback(() => {
    if (timeoutRef.current === null) return;
    const elapsed = Date.now() - startedAtRef.current;
    remainingRef.current = Math.max(remainingRef.current - elapsed, 0);
    clearTimer();
  }, [clearTimer]);

  const syncTimer = useCallback(() => {
    if (hoveredRef.current || focusedRef.current) {
      pauseTimer();
    } else {
      startTimer();
    }
  }, [pauseTimer, startTimer]);

  useEffect(() => {
    startTimer();
    return clearTimer;
    // Timer soll nur beim Mounten starten und beim Unmounten zuverlässig geräumt werden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleMouseEnter() {
    hoveredRef.current = true;
    syncTimer();
  }

  function handleMouseLeave() {
    hoveredRef.current = false;
    syncTimer();
  }

  function handleFocus() {
    focusedRef.current = true;
    syncTimer();
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    focusedRef.current = false;
    syncTimer();
  }

  return (
    <div
      className={`notice notice--${notice.tone}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {notice.tone === "success" ? <CheckCircle2 aria-hidden="true" /> : null}
      {notice.tone === "error" ? <CircleAlert aria-hidden="true" /> : null}
      {notice.tone === "info" ? <Info aria-hidden="true" /> : null}
      <span>{notice.message}</span>
      {notice.action ? (
        <button
          type="button"
          onClick={() => {
            notice.action?.onClick();
            onDismiss(notice.id);
          }}
        >
          {notice.action.label}
        </button>
      ) : null}
      <button type="button" onClick={() => onDismiss(notice.id)} aria-label="Hinweis schließen">
        <X size={17} aria-hidden="true" />
      </button>
    </div>
  );
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [notices, setNotices] = useState<Notice[]>([]);

  const remove = useCallback((id: number) => {
    setNotices((current) => current.filter((notice) => notice.id !== id));
  }, []);

  const notify = useCallback(
    (
      message: string,
      tone: NoticeTone = "success",
      action?: { label: string; onClick: () => void },
    ) => {
      const id = nextNoticeId();
      setNotices((current) => [...current.slice(-2), { id, tone, message, action }]);
      return id;
    },
    [],
  );

  const value = useMemo(() => ({ notify, dismiss: remove }), [notify, remove]);

  const politeNotices = notices.filter((notice) => notice.tone !== "error");
  const assertiveNotices = notices.filter((notice) => notice.tone === "error");

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      {/* Zwei getrennte Live-Regionen statt verschachtelter role="status" pro Meldung:
          eine dezente für Erfolg/Info, eine assertive für Fehler. Gemeinsamer
          Wrapper trägt die Positionierung, damit beide Stacks nicht überlappen. */}
      <div className="notice-stack-region">
        <div className="notice-stack" aria-live="polite" aria-atomic="false">
          {politeNotices.map((notice) => (
            <NoticeItem notice={notice} onDismiss={remove} key={notice.id} />
          ))}
        </div>
        <div className="notice-stack" role="alert">
          {assertiveNotices.map((notice) => (
            <NoticeItem notice={notice} onDismiss={remove} key={notice.id} />
          ))}
        </div>
      </div>
    </NotificationsContext.Provider>
  );
}

// Der Hook gehört bewusst zum Provider und bleibt für eine zentrale, stabile Importstelle in dieser Datei.
// eslint-disable-next-line react-refresh/only-export-components
export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context)
    throw new Error("useNotifications muss innerhalb des NotificationsProvider verwendet werden.");
  return context;
}
